import { useCallback, useEffect, useState } from 'react';
import { Alert } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { collection, doc, getDocs, limit, onSnapshot, query, where, type Query } from 'firebase/firestore';
import type { TonyteActivity } from '../types';
import { DRESDEN_MAY_11_EVENTS } from '../data/dresdnMay11Events';
import { DRESDEN_MAY_12_EVENTS } from '../data/dresdnMay12Events';
import { DRESDEN_MAY_13_17_EVENTS } from '../data/dresdnMay13To17Events';
import { FIRESTORE_COLLECTIONS, firestore, PUBLIC_ACTIVITY_COLLECTIONS } from '../firebase';
import { confirmRemoveEventInterest, setActivityInterested } from '../utils/activityActions';
import { getResolvedEventId } from '../utils/eventIds';
import {
  addDays,
  debugActivityDateDecision,
  isActivityCurrentOrUpcoming,
  shouldDebugFeedDates,
  sortActivitiesByStart,
  startOfLocalDay,
  toLocalDateKey,
} from '../utils/activityDates';
import { isEventActivity } from '../utils/activityKind';
import { shouldShowInMainApp } from '../utils/studiZone';
import { canAccessCity, normalizeCityKey } from '../utils/cities';
import { useI18n } from '../i18n';
import { requireVerifiedEmailForAction } from '../utils/verificationGate';
import { reuseUnchangedActivities } from '../utils/feedStructuralSharing';
import { subscribeShared } from '../utils/sharedFirestoreSubscription';

export const CURATED_EVENTS: TonyteActivity[] = [
  ...DRESDEN_MAY_11_EVENTS,
  ...DRESDEN_MAY_12_EVENTS,
  ...DRESDEN_MAY_13_17_EVENTS,
];

type FeedCacheState = {
  activities: TonyteActivity[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
};

type FeedScope = 'public' | 'admin';

const createInitialFeedCache = (): FeedCacheState => ({
  activities: getCurrentUpcomingPool(CURATED_EVENTS),
  loading: false,
  error: null,
  loaded: false,
});

const feedListeners: Record<FeedScope, Set<(state: FeedCacheState) => void>> = {
  public: new Set(),
  admin: new Set(),
};
let feedCaches: Record<FeedScope, FeedCacheState> = {
  public: createInitialFeedCache(),
  admin: createInitialFeedCache(),
};
let feedLoadPromises: Record<FeedScope, Promise<void> | null> = {
  public: null,
  admin: null,
};
const interestLocalListeners = new Set<() => void>();
const localInterestedByKey = new Map<string, boolean>();
const localCountsByActivityId = new Map<string, number>();

function notifyFeedListeners(scope: FeedScope) {
  feedListeners[scope].forEach(listener => listener(feedCaches[scope]));
}

function setFeedCache(scope: FeedScope, next: Partial<FeedCacheState>) {
  const current = feedCaches[scope];
  const updated = { ...current, ...next };
  const unchanged = (
    updated.activities === current.activities &&
    updated.loading === current.loading &&
    updated.error === current.error &&
    updated.loaded === current.loaded
  );
  if (unchanged) return;
  feedCaches[scope] = updated;
  notifyFeedListeners(scope);
}

const FOCUS_REFRESH_MIN_INTERVAL_MS = 30_000;
const feedLoadedAt: Record<FeedScope, number> = { public: 0, admin: 0 };

function notifyInterestLocalListeners() {
  interestLocalListeners.forEach(listener => listener());
}

function getInterestKey(activityId: string, userId: string) {
  return `${activityId}__${userId}`;
}

type FirestoreActivity = Record<string, any>;
type FirestoreQueryCandidate = {
  label: string;
  target: Query;
};

const VENUE_COORDINATE_FALLBACKS: Array<{
  match: RegExp;
  coordinates: { lat: number; lng: number };
}> = [
  {
    match: /\bcongo\b.*\bgur(?:u)?gaon\b|\bcongo entertainment group\b/i,
    coordinates: { lat: 28.4605492, lng: 77.0953619 },
  },
];

function normalizeCategory(value: unknown): TonyteActivity['category'] {
  const raw = String(value || '').toLowerCase();
  if (raw === 'nightlife') return 'nightlife';
  if (raw === 'food') return 'food';
  if (raw === 'adventure' || raw === 'activities') return 'adventure';
  if (raw === 'social') return 'social';
  return 'culture';
}

function toFiniteNumber(value: unknown): number | null {
  const numberValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(numberValue) ? numberValue : null;
}

function getCoordinatePair(value: unknown): { lat: number; lng: number } | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const lat = toFiniteNumber(raw.lat ?? raw.latitude ?? raw._lat);
  const lng = toFiniteNumber(raw.lng ?? raw.longitude ?? raw.lon ?? raw._long);
  if (lat === null || lng === null) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

function getVenueCoordinateFallback(data: FirestoreActivity): { lat: number; lng: number } | null {
  const text = [
    data.title,
    data.locationName,
    data.venueName,
    data.clubName,
    data.address,
    data.location,
    data.city,
  ].filter(Boolean).join(' ');
  return VENUE_COORDINATE_FALLBACKS.find(item => item.match.test(text))?.coordinates || null;
}

function toDateString(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : toLocalDateKey(parsed);
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
    return toLocalDateKey(value.toDate());
  }
  if (typeof value === 'object' && value !== null && 'seconds' in value && typeof value.seconds === 'number') {
    return toLocalDateKey(new Date(value.seconds * 1000));
  }
  return '';
}

// Firestore Timestamp / Timestamp-like / ISO string -> ISO string. Kept as a
// string (not a Timestamp) so activities stay serializable as nav params.
function toIsoString(value: unknown): string | undefined {
  if (!value) return undefined;
  let date: Date | null = null;
  if (typeof value === 'string') date = new Date(value);
  else if (typeof value === 'number') date = new Date(value);
  else if (typeof value === 'object' && value !== null) {
    const raw = value as { toDate?: () => Date; seconds?: number };
    if (typeof raw.toDate === 'function') date = raw.toDate();
    else if (typeof raw.seconds === 'number') date = new Date(raw.seconds * 1000);
  }
  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : undefined;
}

export function normalizeActivity(id: string, data: FirestoreActivity, sourceCollection?: string): TonyteActivity {
  const coordinates =
    getCoordinatePair(data)
    || getCoordinatePair(data.coordinates)
    || getCoordinatePair(data.location)
    || getCoordinatePair(data.geo)
    || getCoordinatePair(data.geopoint)
    || getCoordinatePair(data.point)
    || getVenueCoordinateFallback(data);

  return {
    id,
    eventId: typeof data.eventId === 'string' ? data.eventId.trim() : undefined,
    canonicalEventId: typeof data.canonicalEventId === 'string' ? data.canonicalEventId.trim() : undefined,
    title: String(data.title || data.name || 'Untitled event'),
    description: String(data.description || data.shortNote || ''),
    locationName: data.locationName || data.venueName || data.clubName || '',
    location: String(data.location || data.address || data.venueName || data.city || 'Dresden'),
    address: String(data.address || data.location || ''),
    city: String(data.city || 'Dresden'),
    cityKey: data.cityKey,
    category: normalizeCategory(data.category || data.eventType),
    entryType: data.entryType === 'community_plan' || data.type === 'plan' || sourceCollection === FIRESTORE_COLLECTIONS.plans ? 'community_plan' : 'official_event',
    type: data.type,
    collection: sourceCollection,
    sourceType:
      data.sourceType === 'club' || data.sourceType === 'generated_jam_session' || data.sourceType === 'user_activity'
        ? data.sourceType
        : 'curated_event',
    hostId: String(data.hostId || data.createdBy || 'system'),
    hostName: String(data.hostName || data.venueName || data.clubName || 'Tonyte'),
    adminId: data.adminId,
    members: Array.isArray(data.members) ? data.members : undefined,
    chatId: data.chatId,
    date: data.date,
    startAt: data.startAt,
    startsAt: data.startsAt,
    endAt: data.endAt,
    endsAt: data.endsAt,
    timestamp: data.timestamp,
    startDate: toDateString(data.startDate || data.date || data.startAt || data.startsAt || data.timestamp),
    endDate: toDateString(data.endDate || data.endAt || data.endsAt) || undefined,
    startTime: data.startTime || data.time ? String(data.startTime || data.time) : '',
    endTime: data.endTime ? String(data.endTime) : undefined,
    coordinates,
    coverPhoto: data.coverPhoto,
    coverImageUrl: data.coverImageUrl,
    coverImage: data.coverImage,
    coverUrl: data.coverUrl,
    photoUrl: data.photoUrl,
    imageUrl: data.imageUrl,
    markerImageUrl: data.markerImageUrl,
    image: data.image,
    imageURL: data.imageURL,
    thumbnailUrl: data.thumbnailUrl,
    posterUrl: data.posterUrl,
    poster: data.poster,
    mediaUrl: data.mediaUrl,
    coverPhotoPath: data.coverPhotoPath,
    imagePath: data.imagePath,
    storagePath: data.storagePath,
    assetsImage: data.assetsImage,
    categoryImage: data.categoryImage,
    backgroundImageUrl: data.backgroundImageUrl,
    approved: data.approved ?? data.isApproved ?? (data.status ? data.status === 'approved' : true),
    isPublic: data.isPublic,
    isFree: Boolean(data.isFree),
    requiresTicket: data.requiresTicket,
    ticketPrice: data.ticketPrice,
    maxParticipants: Number(data.maxParticipants || 0),
    interestedCount: Number(data.interestedCount || 0),
    clubName: data.clubName,
    venueName: data.venueName,
    venueId: data.venueId,
    venueLogoUrl: data.venueLogoUrl,
    venueUsername: data.venueUsername,
    organizerId: data.organizerId,
    organizerName: data.organizerName,
    organizerLogoUrl: data.organizerLogoUrl,
    organizerUsername: data.organizerUsername,
    primaryHostType: data.primaryHostType,
    primaryHostId: data.primaryHostId,
    primaryHostName: data.primaryHostName,
    primaryHostAvatarUrl: data.primaryHostAvatarUrl,
    collaborators: Array.isArray(data.collaborators) ? data.collaborators : [],
    collaboratorProfileKeys: Array.isArray(data.collaboratorProfileKeys) ? data.collaboratorProfileKeys : [],
    postedByType: data.postedByType,
    likesCount: Number(data.likesCount || 0),
    commentsCount: Number(data.commentsCount || 0),
    sharesCount: Number(data.sharesCount || 0),
    placeId: data.placeId,
    lineup: Array.isArray(data.lineup) ? data.lineup : [],
    musicGenres: Array.isArray(data.musicGenres) ? data.musicGenres : [],
    tags: Array.isArray(data.tags) ? data.tags : [],
    hashtags: Array.isArray(data.hashtags) ? data.hashtags : [],
    areaNote: data.areaNote,
    shortNote: data.shortNote,
    sourceLinks: data.sourceLinks,
    officialLink: data.officialLink,
    bannedUserIds: Array.isArray(data.bannedUserIds) ? data.bannedUserIds : [],
    attendees: Array.isArray(data.attendees) ? data.attendees : [],
    participants: typeof data.participants === 'number' ? data.participants : 0,
    participantIds: Array.isArray(data.participantIds) ? data.participantIds : [],
    adminIds: Array.isArray(data.adminIds) ? data.adminIds : [],
    removedUserIds: Array.isArray(data.removedUserIds) ? data.removedUserIds : [],
    createdAt: String(data.createdAt || new Date().toISOString()),
    createdBy: data.createdBy,
    sourceCollection,
    visibility: data.visibility || 'public',
    audience: data.audience,
    zone: data.zone,
    isStudentOnly: data.isStudentOnly === true,
    layer: data.layer,
    // Without these two, shouldShowInMainApp/shouldShowInStudiZone never saw
    // a partner's explicit targeting: StudiZone-only events leaked into the
    // Main App feed and were dropped from StudiZone itself.
    eventVisibility: data.eventVisibility === 'main' || data.eventVisibility === 'studizone' || data.eventVisibility === 'both'
      ? data.eventVisibility
      : undefined,
    audienceDomains: Array.isArray(data.audienceDomains)
      ? data.audienceDomains.filter((d: unknown): d is string => typeof d === 'string' && d.length > 0).map((d: string) => d.toLowerCase())
      : undefined,
    isWeeklyFeatured: data.isWeeklyFeatured === true,
    featuredStartAt: toIsoString(data.featuredStartAt),
    featuredEndAt: toIsoString(data.featuredEndAt),
    featuredPriority: toFiniteNumber(data.featuredPriority) ?? undefined,
    featuredBy: typeof data.featuredBy === 'string' ? data.featuredBy : undefined,
    ownerUid: typeof data.ownerUid === 'string' ? data.ownerUid : undefined,
    partnerId: typeof data.partnerId === 'string' ? data.partnerId : undefined,
    createdByRole: typeof data.createdByRole === 'string' ? data.createdByRole : undefined,
    adminOnly: data.adminOnly === true,
    status: data.status || 'approved',
    deleted: data.deleted === true,
    expired: data.expired === true,
  };
}

function isActivityVisibleForRole(activity: TonyteActivity, role: unknown): boolean {
  if (activity.adminOnly && role !== 'admin' && role !== 'co-admin') return false;
  return canAccessCity(normalizeCityKey(activity.cityKey || activity.city), role);
}

export async function readFirestoreActivities(role: unknown = 'user'): Promise<TonyteActivity[]> {
  const docsById = new Map<string, TonyteActivity>();
  // Load from YESTERDAY's start date, not today's: an event that started
  // yesterday and is still running (e.g. 22:00–04:00) must stay visible in
  // "The Vibe Continues" until it ends. Ended ones are dropped by the usual
  // current-or-upcoming / selected-date checks downstream.
  const fromKey = toLocalDateKey(addDays(new Date(), -1));
  if (__DEV__) {
    console.log('[Firebase config]', {
      projectId: firestore.app.options.projectId,
      appId: firestore.app.options.appId,
      environment: __DEV__ ? 'development' : 'production',
      collectionPaths: PUBLIC_ACTIVITY_COLLECTIONS,
    });
  }
  const stageCounts = { raw: 0, approvedAndPublished: 0, isEvent: 0, visibleForRole: 0, notStudiZoneOnly: 0 };
  const addSnapshotDocs = (label: string, docs: Array<{ id: string; data: () => FirestoreActivity }>) => {
    docs.forEach(docSnap => {
      stageCounts.raw += 1;
      const collectionName = label.split('.')[0];
      const activity = normalizeActivity(docSnap.id, docSnap.data(), collectionName);
      const resolvedEventId = getResolvedEventId(activity);
      const approved = activity.approved !== false && activity.status !== 'rejected';
      if (activity.visibility === 'private' || !approved) return;
      stageCounts.approvedAndPublished += 1;
      if (!isEventActivity(activity)) return;
      stageCounts.isEvent += 1;
      if (!isActivityVisibleForRole(activity, role)) return;
      stageCounts.visibleForRole += 1;
      // StudiZone-only events (eventVisibility === 'studizone', set only by
      // University/Student Organization partners) are excluded from this
      // shared Main App cache — Feed, Map, and EventDetailScreen (which
      // reads from this same cache) all inherit the exclusion from here.
      // Never newly excludes a legacy event with no eventVisibility field.
      if (!shouldShowInMainApp(activity)) return;
      stageCounts.notStudiZoneOnly += 1;
      docsById.set(resolvedEventId, activity);
    });
  };
  const runCandidates = async (candidates: FirestoreQueryCandidate[]) => {
    let hadSuccessfulQuery = false;
    for (const candidate of candidates) {
      try {
        const snapshot = await getDocs(candidate.target);
        hadSuccessfulQuery = true;
        addSnapshotDocs(candidate.label, snapshot.docs as Array<{ id: string; data: () => FirestoreActivity }>);
        if (docsById.size > 0) return { loaded: true, hadSuccessfulQuery };
      } catch (error) {
        console.warn('[Tonyte][RN][Feed] Firestore query skipped', {
          label: candidate.label,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
    return { loaded: docsById.size > 0, hadSuccessfulQuery };
  };

  for (const collectionName of PUBLIC_ACTIVITY_COLLECTIONS) {
    const ref = collection(firestore, collectionName);
    if (role === 'admin') {
      // Date-filtered first, same as the public path below — an unordered
      // `limit(300)` scan (the old primary query) can be starved entirely by
      // a block of stale documents that happens to sort first by document
      // ID, hiding every current/upcoming event behind them.
      const primary = await runCandidates([
        {
          label: `${collectionName}.admin.currentOrFutureStartDate`,
          target: query(ref, where('startDate', '>=', fromKey), limit(500)),
        },
        {
          label: `${collectionName}.admin.currentOrFutureDate`,
          target: query(ref, where('date', '>=', fromKey), limit(500)),
        },
      ]);
      if (primary.loaded) break;
      if (primary.hadSuccessfulQuery) continue;
      const fallback = await runCandidates([
        { label: `${collectionName}.base`, target: query(ref, limit(300)) },
        { label: `${collectionName}.isPublic`, target: query(ref, where('isPublic', '==', true), limit(300)) },
        { label: `${collectionName}.approved`, target: query(ref, where('approved', '==', true), limit(300)) },
        { label: `${collectionName}.statusApproved`, target: query(ref, where('status', '==', 'approved'), limit(300)) },
      ]);
      if (fallback.loaded) break;
      continue;
    }
    const primary = await runCandidates([
      {
        label: `${collectionName}.currentOrFutureStartDate`,
        target: query(ref, where('startDate', '>=', fromKey), limit(500)),
      },
      {
        label: `${collectionName}.currentOrFutureDate`,
        target: query(ref, where('date', '>=', fromKey), limit(500)),
      },
    ]);
    if (primary.loaded) break;
    if (primary.hadSuccessfulQuery) continue;
    const dresdenFilters = [
      { label: 'cityKeyDresden', constraint: where('cityKey', '==', 'dresden') },
      { label: 'cityDresden', constraint: where('city', '==', 'Dresden') },
    ];
    const fallback = await runCandidates(dresdenFilters.flatMap(cityFilter => [
      { label: `${collectionName}.${cityFilter.label}.base`, target: query(ref, cityFilter.constraint, limit(300)) },
      { label: `${collectionName}.${cityFilter.label}.isPublic`, target: query(ref, cityFilter.constraint, where('isPublic', '==', true), limit(300)) },
      { label: `${collectionName}.${cityFilter.label}.approved`, target: query(ref, cityFilter.constraint, where('approved', '==', true), limit(300)) },
      { label: `${collectionName}.${cityFilter.label}.statusApproved`, target: query(ref, cityFilter.constraint, where('status', '==', 'approved'), limit(300)) },
    ]));
    if (fallback.loaded) break;
  }

  const rawDocs = Array.from(docsById.values());
  if (__DEV__) {
    console.log('[Events] raw Firestore:', stageCounts.raw);
    console.log('[Events] approved + not-private:', stageCounts.approvedAndPublished);
    console.log('[Events] isEventActivity:', stageCounts.isEvent);
    console.log('[Events] visible for role/city:', stageCounts.visibleForRole);
    console.log('[Events] not StudiZone-only (final shared cache):', stageCounts.notStudiZoneOnly);
    if (stageCounts.raw > 0 && rawDocs.length === 0) {
      console.warn('[Tonyte][RN][Feed] every raw doc was filtered out — falling back to CURATED_EVENTS. This is the exact failure mode reported as "No mapped events nearby": a real query succeeded but every result was rejected downstream.');
    }
  }
  return rawDocs;
}

function getCurrentUpcomingPool(events: TonyteActivity[]): TonyteActivity[] {
  const now = new Date();
  const today = startOfLocalDay(now);
  const debugFeedDates = shouldDebugFeedDates();
  if (debugFeedDates) {
    console.log('[Tonyte][RN][FeedDate] shared pool', {
      currentDeviceDate: now.toISOString(),
      selectedFeedDate: toLocalDateKey(today),
      inputCount: events.length,
    });
  }
  return events
    .filter(activity => {
      const included = isActivityCurrentOrUpcoming(activity, now);
      if (debugFeedDates) {
        debugActivityDateDecision(
          activity,
          today,
          included,
          now,
          included ? 'shared_pool_current_or_upcoming' : 'shared_pool_expired',
        );
      }
      return included;
    })
    .sort(sortActivitiesByStart);
}

async function loadFeedCache(scope: FeedScope, force = false) {
  if (feedLoadPromises[scope] && !force) return feedLoadPromises[scope];
  if (feedCaches[scope].loaded && !force) return Promise.resolve();

  feedLoadPromises[scope] = (async () => {
    if (__DEV__) console.log('[Perf][Events] subscription started', { scope, force });
    // Background refreshes of already-loaded data stay silent (consumers only
    // surface `loading` before the first load), so skip the extra re-renders.
    if (!feedCaches[scope].loaded) setFeedCache(scope, { loading: true, error: null });
    const perfStart = performance.now();
    try {
      const role = scope === 'admin' ? 'admin' : 'user';
      const firestoreEvents = await readFirestoreActivities(role);
      if (__DEV__) {
        console.log(`[Perf][Events] snapshot received: ${firestoreEvents.length}`, { scope });
        console.log(`[Perf][Events] normalization complete: ${(performance.now() - perfStart).toFixed(1)}ms`, { scope });
      }
      const source = firestoreEvents.length ? firestoreEvents : CURATED_EVENTS;
      const pooled = getCurrentUpcomingPool(source);
      setFeedCache(scope, {
        activities: reuseUnchangedActivities(feedCaches[scope].activities, pooled),
        loaded: true,
        error: null,
      });
      feedLoadedAt[scope] = Date.now();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn('[Tonyte][RN][Feed] Firestore load failed, using curated fallback', message);
      setFeedCache(scope, {
        activities: getCurrentUpcomingPool(CURATED_EVENTS),
        loaded: true,
        error: message,
      });
    } finally {
      setFeedCache(scope, { loading: false });
      feedLoadPromises[scope] = null;
    }
  })();

  return feedLoadPromises[scope];
}

export function useFeed(options?: { includeAdminOnlyCities?: boolean }) {
  const scope: FeedScope = options?.includeAdminOnlyCities ? 'admin' : 'public';
  const [state, setState] = useState<FeedCacheState>(() => feedCaches[scope]);

  useEffect(() => {
    feedListeners[scope].add(setState);
    setState(feedCaches[scope]);
    void loadFeedCache(scope);
    return () => {
      feedListeners[scope].delete(setState);
    };
  }, [scope]);

  // Once feedCaches[scope].loaded is true, loadFeedCache() above is a no-op
  // for the rest of the app session (see its own comment) — a screen kept
  // alive by React Navigation's tab caching would otherwise show whatever
  // was fetched at first launch for as long as the app stays open, with no
  // way to see edits/deletes made elsewhere (the Partner Dashboard website,
  // another admin, another device) short of a manual pull-to-refresh or a
  // full app relaunch. Silently re-fetch (at most every 30s) when a screen using this
  // hook regains focus instead. This never blocks the UI: `loading` below
  // only reflects the very first, not-yet-loaded fetch (`!state.loaded`),
  // so already-cached data stays visible the whole time this runs.
  useFocusEffect(
    useCallback(() => {
      // Returning from a pushed screen (event detail, chat, …) refocuses every
      // mounted tab at once; don't refetch + re-normalize if we just did.
      if (feedCaches[scope].loaded && Date.now() - feedLoadedAt[scope] >= FOCUS_REFRESH_MIN_INTERVAL_MS) {
        void loadFeedCache(scope, true);
      }
    }, [scope]),
  );

  const refresh = useCallback(async () => {
    await loadFeedCache(scope, true);
  }, [scope]);

  return {
    activities: state.activities,
    loading: state.loading && !state.loaded,
    error: state.error,
    refresh,
  };
}

export function removeActivityFromFeedCache(activityId: string) {
  (Object.keys(feedCaches) as FeedScope[]).forEach(scope => {
    setFeedCache(scope, {
      activities: feedCaches[scope].activities.filter(activity => activity.id !== activityId),
    });
  });
}

export function updateActivityInFeedCache(updatedActivity: TonyteActivity) {
  let result = { updatedExisting: false, cacheCount: 0 };
  (Object.keys(feedCaches) as FeedScope[]).forEach(scope => {
    const role = scope === 'admin' ? 'admin' : 'user';
    let didUpdate = false;
    const nextActivities = feedCaches[scope].activities.map(activity => {
      if (activity.id !== updatedActivity.id) return activity;
      didUpdate = true;
      return { ...activity, ...updatedActivity };
    });
    const candidate = didUpdate ? nextActivities : [updatedActivity, ...feedCaches[scope].activities];
    const activities = getCurrentUpcomingPool(candidate.filter(activity => isActivityVisibleForRole(activity, role)));
    setFeedCache(scope, { activities });
    if (scope === 'public') {
      result = { updatedExisting: didUpdate, cacheCount: activities.length };
    }
  });
  if (__DEV__) {
    console.log('[Tonyte][RN][EventEdit] local cache update result', {
      eventId: updatedActivity.id,
      updatedExisting: result.updatedExisting,
      cacheCount: result.cacheCount,
    });
  }
  return result;
}

export function useActivityInterested(
  activityId: string,
  userId: string | null,
  activity?: TonyteActivity,
) {
  const { t } = useI18n();
  const resolvedEventId = getResolvedEventId(activity) || activityId;
  const [interested, setInterested] = useState(false);
  const [count, setCount] = useState(0);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const applyLocalState = () => {
      if (userId) {
        const localInterested = localInterestedByKey.get(getInterestKey(resolvedEventId, userId));
        if (typeof localInterested === 'boolean') setInterested(localInterested);
      }
      const localCount = localCountsByActivityId.get(resolvedEventId);
      if (typeof localCount === 'number') setCount(localCount);
    };
    interestLocalListeners.add(applyLocalState);
    applyLocalState();
    return () => {
      interestLocalListeners.delete(applyLocalState);
    };
  }, [resolvedEventId, userId]);

  useEffect(() => {
    setCount(0);
  }, [resolvedEventId]);

  useEffect(() => {
    if (!activityId || !userId) {
      setInterested(false);
      return undefined;
    }
    const cached = localInterestedByKey.get(getInterestKey(resolvedEventId, userId));
    const initial = typeof cached === 'boolean' ? cached : activity?.viewerInterested === true;
    localInterestedByKey.set(getInterestKey(resolvedEventId, userId), initial);
    setInterested(initial);
    notifyInterestLocalListeners();
    return undefined;
  }, [activity?.viewerInterested, activityId, resolvedEventId, userId]);

  useEffect(() => {
    if (!resolvedEventId) return;
    // Seed from cache (populated by a previous toggle on this session) or fall
    // back to the interestedCount field on the event document. The optimistic
    // toggle keeps the local count accurate after the first interaction, so no
    // subcollection query is needed.
    const cached = localCountsByActivityId.get(resolvedEventId);
    if (typeof cached === 'number') {
      setCount(cached);
      return;
    }
    const initial = Math.max(
      typeof activity?.interestedCount === 'number' ? activity.interestedCount : 0,
      activity?.viewerInterested ? 1 : 0,
    );
    localCountsByActivityId.set(resolvedEventId, initial);
    setCount(initial);
    notifyInterestLocalListeners();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedEventId, activity?.interestedCount]);

  // Server truth for the count: eventStats/{id}.interestedCount is recounted
  // by a Cloud Function on every interest write (the event doc's own
  // interestedCount field is never maintained). One shared listener per event
  // no matter how many cards/screens show it.
  useEffect(() => {
    if (!resolvedEventId) return undefined;
    return subscribeShared<number | null>(
      `eventStats:${resolvedEventId}`,
      (onData, onError) => onSnapshot(
        doc(firestore, 'eventStats', resolvedEventId),
        snapshot => onData(snapshot.exists() ? Number(snapshot.data().interestedCount || 0) : null),
        onError,
      ),
      serverCount => {
        if (serverCount === null || localCountsByActivityId.get(resolvedEventId) === serverCount) return;
        localCountsByActivityId.set(resolvedEventId, serverCount);
        notifyInterestLocalListeners();
      },
      () => undefined,
    );
  }, [resolvedEventId]);

  // Server truth for "am I interested": one app-wide listener on the viewer's
  // own activityInterests docs (outside Profile, events don't carry
  // viewerInterested). Local writes show up here immediately (latency
  // compensation), so optimistic toggles stay consistent.
  useEffect(() => {
    if (!resolvedEventId || !userId) return undefined;
    return subscribeShared<Set<string>>(
      `myActivityInterests:${userId}`,
      (onData, onError) => onSnapshot(
        query(collection(firestore, FIRESTORE_COLLECTIONS.activityInterests), where('userId', '==', userId)),
        snapshot => onData(new Set(snapshot.docs.map(interestDoc => String(interestDoc.get('activityId') || '')))),
        onError,
      ),
      interestedIds => {
        const key = getInterestKey(resolvedEventId, userId);
        const next = interestedIds.has(resolvedEventId);
        if (localInterestedByKey.get(key) === next) return;
        localInterestedByKey.set(key, next);
        notifyInterestLocalListeners();
      },
      () => undefined,
    );
  }, [resolvedEventId, userId]);

  const toggle = useCallback(async () => {
    if (!activity) {
      console.warn('[Tonyte][RN][Interest] toggle skipped missing activity', {
        activityId,
        action: 'setActivityInterested',
      });
      return;
    }
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('auth.signInToUseFeature'));
      return;
    }

    const nextInterested = !interested;
    if (!nextInterested && !await confirmRemoveEventInterest(activity, userId, t)) return false;
    // Only the "mark interested" direction is gated — removing your own
    // interest is safety-neutral, not a social write. requireVerifiedEmailForAction
    // shows its own alert (rather than throwing) since the catch below
    // rolls back the optimistic UI state silently with no other feedback.
    if (nextInterested && !requireVerifiedEmailForAction()) return false;
    const nextCount = Math.max(0, count + (nextInterested ? 1 : -1));
    setPending(true);
    localInterestedByKey.set(getInterestKey(resolvedEventId, userId), nextInterested);
    localCountsByActivityId.set(resolvedEventId, nextCount);
    setInterested(nextInterested);
    setCount(nextCount);
    notifyInterestLocalListeners();
    try {
      await setActivityInterested(activity, userId, nextInterested, t);
      return true;
    } catch {
      localInterestedByKey.set(getInterestKey(resolvedEventId, userId), !nextInterested);
      localCountsByActivityId.set(resolvedEventId, Math.max(0, nextCount + (nextInterested ? -1 : 1)));
      setInterested(!nextInterested);
      setCount(Math.max(0, nextCount + (nextInterested ? -1 : 1)));
      notifyInterestLocalListeners();
      return false;
    } finally {
      setPending(false);
    }
  }, [activity, activityId, count, interested, resolvedEventId, t, userId]);

  const syncInterested = useCallback((nextInterested: boolean) => {
    if (!userId || nextInterested === interested) return;
    const nextCount = Math.max(0, count + (nextInterested ? 1 : -1));
    localInterestedByKey.set(getInterestKey(resolvedEventId, userId), nextInterested);
    localCountsByActivityId.set(resolvedEventId, nextCount);
    setInterested(nextInterested);
    setCount(nextCount);
    notifyInterestLocalListeners();
  }, [count, interested, resolvedEventId, userId]);

  return { interested, count, pending, toggle, syncInterested };
}
